/**
 * Estatísticas de autonomia de uma boia, derivadas do histórico de `status`.
 *
 * **Porquê derivar no cliente:** o nó já manda tudo o que é preciso — uptime,
 * potência e estado de carga viajam em cada bloco `status` — e o teste de
 * resistência está a correr *agora*; mexer no firmware ou na API a meio
 * invalidava a própria medição. O que falta é só aritmética sobre a janela
 * escolhida, e essa pertence a quem desenha.
 *
 * Tudo aqui é sobre o **intervalo selecionado**, não sobre a vida da boia:
 * mudar o intervalo muda as respostas, tal como nos gráficos ao lado.
 */

import type { StatusPoint } from "../types";

/**
 * Um período contínuo de funcionamento, delimitado por recuos do uptime.
 *
 * A `duracao` é o uptime do último bloco da sessão, portanto conta desde o
 * **arranque real**, mesmo que esse arranque seja anterior ao intervalo
 * escolhido — uma sessão de três dias vista numa janela de 24 h aparece com os
 * três dias, não com o pedaço que coube na janela.
 */
export interface Sessao {
  /** Instante estimado do arranque: último sinal − duração. */
  inicio: number;
  /** Instante (ms) do último bloco de estado visto nesta sessão. */
  ultimoSinal: number;
  /** Segundos entre o arranque e o último sinal. */
  duracao: number;
}

export interface Autonomia {
  /** Segundos desde o último arranque, do ponto mais recente. `null` sem pontos. */
  ligadoHa: number | null;
  /** Quantas vezes o uptime recuou dentro do intervalo — cada recuo é um reboot. */
  reinicios: number;
  /** Sessões vistas no intervalo, por ordem do tempo. A última pode estar em curso. */
  sessoes: Sessao[];
  /** Potência média ponderada no tempo, em mW. `null` sem segmentos válidos. */
  consumoMedioMw: number | null;
  /** Energia consumida no intervalo, em Wh, integrada por trapézios. */
  energiaWh: number | null;
  /** Declive da bateria em %/h (negativo = a descarregar). `null` se instável. */
  descargaPctPorHora: number | null;
  /** Último estado de carga conhecido no intervalo, 0–100. */
  pctAtual: number | null;
  /** Horas até 0 % à taxa atual. Só quando a descarga é claramente negativa. */
  horasRestantes: number | null;
}

const VAZIA: Autonomia = {
  ligadoHa: null,
  reinicios: 0,
  sessoes: [],
  consumoMedioMw: null,
  energiaWh: null,
  descargaPctPorHora: null,
  pctAtual: null,
  horasRestantes: null,
};

/**
 * Um buraco no uplink não é consumo zero: segmentos entre pontos afastados
 * mais do que isto ficam fora do integral, em vez de esticarem o trapézio
 * por cima do silêncio.
 */
const SEGMENTO_MAX_MS = 10 * 60_000;

/** Abaixo disto o declive é ruído do contador, não descarga. */
const DESCARGA_MINIMA_PCT_H = 0.1;

/** Com menos amostragem do que isto, a reta da bateria mente. */
const PONTOS_MINIMOS = 5;
const JANELA_MINIMA_MS = 30 * 60_000;

export function autonomia(status: StatusPoint[]): Autonomia {
  if (status.length === 0) return VAZIA;

  const ultimo = status[status.length - 1]!;

  // Sessões: corta-se onde o uptime recua. O relógio do nó pode derivar, mas
  // o uptime é monótono dentro de um arranque — um recuo só tem uma causa.
  const sessoes: Sessao[] = [];
  let fimSessao = status[0]!;
  for (let i = 1; i <= status.length; i++) {
    const p = status[i];
    if (p !== undefined && p.uptime >= fimSessao.uptime) {
      fimSessao = p;
      continue;
    }
    const ultimoSinal = Date.parse(fimSessao.timestamp);
    sessoes.push({
      inicio: ultimoSinal - fimSessao.uptime * 1000,
      ultimoSinal,
      duracao: fimSessao.uptime,
    });
    if (p !== undefined) fimSessao = p;
  }
  const reinicios = sessoes.length - 1;

  // Integral de potência por trapézios, saltando buracos: mede o que se viu,
  // não inventa o que não chegou.
  let energiaMwMs = 0;
  let duracaoMs = 0;
  for (let i = 1; i < status.length; i++) {
    const a = status[i - 1]!;
    const b = status[i]!;
    const dt = Date.parse(b.timestamp) - Date.parse(a.timestamp);
    if (dt <= 0 || dt > SEGMENTO_MAX_MS) continue;
    energiaMwMs += ((a.power_mw + b.power_mw) / 2) * dt;
    duracaoMs += dt;
  }
  const consumoMedioMw = duracaoMs > 0 ? energiaMwMs / duracaoMs : null;
  const energiaWh = duracaoMs > 0 ? energiaMwMs / 3_600_000_000 : null;

  // Reta de mínimos quadrados sobre (horas, %). Mais robusta do que
  // (último − primeiro): sobrevive a um balde esquisito nas pontas.
  const comCarga = status.filter((p) => p.battery_pct !== null);
  const pctAtual = comCarga.length > 0 ? comCarga[comCarga.length - 1]!.battery_pct : null;

  let descargaPctPorHora: number | null = null;
  if (comCarga.length >= PONTOS_MINIMOS) {
    const t0 = Date.parse(comCarga[0]!.timestamp);
    const tFim = Date.parse(comCarga[comCarga.length - 1]!.timestamp);
    if (tFim - t0 >= JANELA_MINIMA_MS) {
      let somaX = 0;
      let somaY = 0;
      let somaXX = 0;
      let somaXY = 0;
      for (const p of comCarga) {
        const x = (Date.parse(p.timestamp) - t0) / 3_600_000;
        const y = p.battery_pct!;
        somaX += x;
        somaY += y;
        somaXX += x * x;
        somaXY += x * y;
      }
      const n = comCarga.length;
      const variancia = n * somaXX - somaX * somaX;
      if (variancia > 0) descargaPctPorHora = (n * somaXY - somaX * somaY) / variancia;
    }
  }

  // A estimativa só aparece quando há descarga franca: com a boia em carga ou
  // estável, "horas restantes" seria um número aleatório com unidade.
  const horasRestantes =
    descargaPctPorHora !== null &&
    descargaPctPorHora < -DESCARGA_MINIMA_PCT_H &&
    pctAtual !== null
      ? pctAtual / -descargaPctPorHora
      : null;

  return {
    ligadoHa: ultimo.uptime,
    reinicios,
    sessoes,
    consumoMedioMw,
    energiaWh,
    descargaPctPorHora,
    pctAtual,
    horasRestantes,
  };
}
